import React from 'react'
import {Trans} from 'react-i18next'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Typography,
} from '@mui/material'

import {
  CheckboxQuestion,
  questionTitleStyles,
} from '@progyny/pages/Consultations/components/questions/types'

interface CheckboxQuestionRendererProps {
  question: CheckboxQuestion
  formData: Record<string, string>
  handleInputChange: (questionId: string, value: string) => void
  isSubmitting: boolean
  colors: {primary: string; secondary: string}
}

const CheckboxQuestionRenderer: React.FC<CheckboxQuestionRendererProps> = ({
  question,
  formData,
  handleInputChange,
  isSubmitting,
  colors,
}) => {
  const selectedValues = formData[question.id]
    ? formData[question.id].split(',')
    : []

  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const updated = checked
      ? [...selectedValues, optionValue]
      : selectedValues.filter((item) => item !== optionValue)
    handleInputChange(question.id, updated.join(','))
  }

  return (
    <FormControl
      component='fieldset'
      fullWidth
      sx={{maxWidth: '640px', margin: '0 auto'}}
    >
      <FormLabel
        component='legend'
        sx={{width: '100%', display: 'block', marginBottom: '38px'}}
      >
        <Typography sx={{...questionTitleStyles, marginBottom: '8px'}}>
          <Trans>{question.title}</Trans>
        </Typography>
        {question.description && (
          <Typography variant='body2' color='text.secondary' textAlign='center'>
            <Trans>{question.description}</Trans>
          </Typography>
        )}
      </FormLabel>
      <FormGroup sx={{gap: '12px'}}>
        {question.options.map((option) => {
          const isSelected = selectedValues.includes(option.value)
          return (
            <Box
              key={option.value}
              sx={{
                border: isSelected
                  ? `2px solid ${colors.primary}`
                  : '2px solid #EAECF0',
                borderRadius: '12px',
                backgroundColor: isSelected ? colors.secondary : 'transparent',
                padding: '20px 16px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: isSubmitting ? undefined : colors.primary,
                },
              }}
              onClick={(e) => {
                e.preventDefault()
                if (!isSubmitting) {
                  handleCheckboxChange(option.value, !isSelected)
                }
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSelected}
                    disabled={isSubmitting}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                  />
                }
                label={option.label}
                sx={{
                  margin: 0,
                  width: '100%',
                  pointerEvents: 'none',
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                    color: isSelected ? colors.primary : 'inherit',
                    fontWeight: isSelected ? 600 : 'inherit',
                  },
                }}
              />
            </Box>
          )
        })}
      </FormGroup>
    </FormControl>
  )
}

export default CheckboxQuestionRenderer
